import { useCallback } from 'react'
import type { GridApi, ModelUpdatedEvent } from 'ag-grid-community'

import { autoSizeInitialColumns } from '@/containers/DataTable/columnGroupSizing'
import { serializeColumnStateForContext } from '@/lib/agGridColumnState'
import { useColumnStateLifecycle } from '@/containers/DataTable/useColumnStateLifecycle'

/** DataTable's grid callbacks share a guard for the committed activation. */
export const useColumnStateCallbacks = (
  gridApi: GridApi | null,
  contextKey: string | null,
  storageKey: string | null
) => {
  const columnStateLifecycle = useColumnStateLifecycle(contextKey)
  const saveColumnState = useCallback(
    (api = gridApi) => {
      if (
        !columnStateLifecycle.isActive() ||
        !columnStateLifecycle.initialized ||
        !api ||
        !contextKey ||
        !storageKey
      )
        return

      try {
        const columnState = api.getColumnState()
        const storedValue = localStorage.getItem(storageKey)
        localStorage.setItem(
          storageKey,
          serializeColumnStateForContext(storedValue, contextKey, columnState)
        )
      } catch (error) {
        console.warn('Failed to save column state to localStorage:', error)
      }
    },
    [gridApi, contextKey, storageKey, columnStateLifecycle]
  )

  const onInitialModelUpdated = useCallback(
    (params: ModelUpdatedEvent) => {
      if (!columnStateLifecycle.isActive()) return
      if (
        !autoSizeInitialColumns(
          params,
          columnStateLifecycle.pendingInitialAutoSize ? contextKey : null,
          contextKey
        )
      )
        return

      columnStateLifecycle.pendingInitialAutoSize = false
      columnStateLifecycle.initialized = true
      saveColumnState(params.api)
    },
    [contextKey, columnStateLifecycle, saveColumnState]
  )

  return { columnStateLifecycle, saveColumnState, onInitialModelUpdated }
}
